import { ShoppingCart, Menu, X, Building2, Search, Heart, ChevronDown, Bath, CookingPot, Grid2x2, Lightbulb, Zap, DoorOpen, Sparkles, Armchair, Thermometer, type LucideIcon } from "lucide-react";
import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCartStore } from "@/stores/cartStore";
import { useWishlistStore } from "@/stores/wishlistStore";
import { CartDrawer } from "@/components/cart/CartDrawer";
import { WishlistDrawer } from "@/components/wishlist/WishlistDrawer";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { AnnouncementBar } from "@/components/layout/AnnouncementBar";

import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { fetchProducts, ShopifyProduct } from "@/lib/shopify";
import { getLocalizedDescription, getLocalizedTitle, type Language } from "@/lib/productTranslations";
import { expandSearchTerms } from "@/lib/searchTerms";
import { useCategoryTranslations } from "@/hooks/useCategoryTranslations";
import { supabase } from "@/integrations/supabase/client";
import { useBrands } from "@/hooks/useBrands";
import menuLightingImg from "@/assets/menu-lighting.jpg";
import menuElectricityImg from "@/assets/menu-electricity.jpg";
import menuHardwareImg from "@/assets/menu-hardware.jpg";
import menuAccessoriesImg from "@/assets/menu-accessories.jpg";
import menuKitchenImg from "@/assets/menu-kitchen.jpg";
import menuBathroomImg from "@/assets/menu-bathroom.jpg";
import hausLogo from "@/assets/haus-logo.png";
import { buildCategoryPath } from "@/lib/catalogUrl";

interface DbMenuItem {
  category: string;
  subcategory: string;
  group_name: string | null;
  label_en: string;
  label_lv: string | null;
  label_ru: string | null;
  image_url?: string | null;
  is_active: boolean;
  sort_order: number | null;
}

// Icon shown to the left of each top-level category
const CATEGORY_ICONS: Record<string, LucideIcon> = {
  Bathroom: Bath,
  Kitchen: CookingPot,
  Tiles: Grid2x2,
  Lighting: Lightbulb,
  Electricity: Zap,
  Heating: Thermometer,
  Hardware: DoorOpen,
  Accessories: Sparkles,
  "Life Style": Armchair,
};

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const [dbMenuItems, setDbMenuItems] = useState<DbMenuItem[]>([]);
  const [groupTranslations, setGroupTranslations] = useState<Record<string, { label_en: string | null; label_lv: string | null; label_ru: string | null }>>({});
  const { translateCategory: translateCategoryFromDb } = useCategoryTranslations();
  const totalItems = useCartStore((state) => state.getTotalItems());
  const setCartOpen = useCartStore((state) => state.setOpen);
  const wishlistItems = useWishlistStore((state) => state.getTotalItems());
  const setWishlistOpen = useWishlistStore((state) => state.setOpen);
  const navigate = useNavigate();
  const { t, getLocalizedPath, language } = useLanguage();
  const { brands } = useBrands();
  const { isBusinessCustomer, isApprovedBusinessCustomer } = useAuth();

  // Helper to get translated group title
  const getGroupTitle = (category: string, groupName: string): string => {
    const key = `${category}|${groupName}`;
    const trans = groupTranslations[key];
    if (trans) {
      if (language === 'lv' && trans.label_lv) return trans.label_lv;
      if (language === 'ru' && trans.label_ru) return trans.label_ru;
      if (trans.label_en) return trans.label_en;
    }
    return groupName;
  };
  // Helper to get translated category label with DB fallback to i18n
  const getCategoryLabel = (category: string, i18nFallback: string): string => {
    const dbLabel = translateCategoryFromDb(category, language as 'en' | 'lv' | 'ru');
    return dbLabel !== category ? dbLabel : (i18nFallback || category);
  };


    const getCategoryNameFromLabel = (label: string): string => {
    if (label === t.categories.kitchens) return "Kitchen";
    if (label === t.categories.lighting) return "Lighting";
    if (label === t.categories.electricity) return "Electricity";
    if (label === t.categories.hardware) return "Hardware";
    if (label === t.categories.accessories) return "Accessories";
    return label;
  };

  // Fetch menu items and group translations from DB
  useEffect(() => {
    supabase
      .from('menu_items')
      .select('*')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (data) setDbMenuItems(data as DbMenuItem[]);
      });
    
    supabase
      .from('menu_group_translations')
      .select('*')
      .then(({ data }) => {
        if (data) {
          const map: Record<string, { label_en: string | null; label_lv: string | null; label_ru: string | null }> = {};
          data.forEach((row: any) => {
            map[`${row.category}|${row.group_name}`] = {
              label_en: row.label_en,
              label_lv: row.label_lv,
              label_ru: row.label_ru,
            };
          });
          setGroupTranslations(map);
        }
      });
  }, []);

  // Search products with multi-language support
  // We fetch products without a Shopify query and filter client-side to support LV/RU titles
  // Handle search form submit - navigate to shop page with search query
  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (searchQuery.trim()) {
      navigate(getLocalizedPath(`/shop?search=${encodeURIComponent(searchQuery.trim())}`));
      setSearchOpen(false);
      setSearchQuery("");
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  // Close search on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Category metadata (images, isLarge flag)
  const categoryMeta: Record<string, { image: any; isLarge: boolean }> = {
    Bathroom: { image: menuBathroomImg, isLarge: true },
    Kitchen: { image: menuKitchenImg, isLarge: true },
    Lighting: { image: menuLightingImg, isLarge: false },
    Electricity: { image: menuElectricityImg, isLarge: false },
    Hardware: { image: menuHardwareImg, isLarge: false },
    Accessories: { image: menuAccessoriesImg, isLarge: false },
  };

  // Preferred category display order
  const CATEGORY_ORDER = ["Bathroom", "Kitchen", "Tiles", "Lighting", "Electricity", "Hardware", "Accessories"];

  // Build categories entirely from DB menu items (fully dynamic)
  const mergedCategories = useMemo(() => {
    if (dbMenuItems.length === 0) return [];

    // Group DB items by category
    const categoryMap = new Map<string, DbMenuItem[]>();
    dbMenuItems.forEach(item => {
      if (!categoryMap.has(item.category)) categoryMap.set(item.category, []);
      categoryMap.get(item.category)!.push(item);
    });

    // Sort categories by preferred order, then alphabetically for unknown ones
    const sortedCategories = Array.from(categoryMap.keys()).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.localeCompare(b);
    });

    return sortedCategories.map(catName => {
      const items = categoryMap.get(catName)!;
      const meta = categoryMeta[catName] || { image: undefined, isLarge: false };

      // Group items by group_name, preserving insertion order (sort_order already applied by query)
      const groupMap = new Map<string, DbMenuItem[]>();
      items.forEach(item => {
        const gn = item.group_name || '';
        if (!groupMap.has(gn)) groupMap.set(gn, []);
        groupMap.get(gn)!.push(item);
      });

      const groups = Array.from(groupMap.entries()).map(([groupName, groupItems]) => {
        const translatedTitle = groupName ? getGroupTitle(catName, groupName) : '';
        return {
          title: translatedTitle,
          href: groupName
            ? getLocalizedPath(buildCategoryPath(catName, groupName))
            : getLocalizedPath(buildCategoryPath(catName)),
          items: groupItems.map(gi => {
            const label = language === 'lv' ? (gi.label_lv || gi.label_en)
              : language === 'ru' ? (gi.label_ru || gi.label_en)
              : gi.label_en;
            return {
              label,
              image: gi.image_url || null,
              href: getLocalizedPath(buildCategoryPath(gi.category, gi.subcategory)),
            };
          }),
        };
      });

      return {
        label: getCategoryLabel(catName, (t.categories as any)[catName.toLowerCase()] || catName),
        href: getLocalizedPath(buildCategoryPath(catName)),
        image: meta.image,
        icon: CATEGORY_ICONS[catName],
        isLarge: meta.isLarge || groups.length > 1,
        groups,
      };
    });
  }, [dbMenuItems, language, t, getLocalizedPath, groupTranslations, translateCategoryFromDb]);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled 
        ? 'glass shadow-soft' 
        : 'bg-transparent'
    }`}>
      <AnnouncementBar />
      <div className={`container mx-auto px-4 transition-all duration-500 ${scrolled ? 'py-3' : 'py-5'}`}>

        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to={getLocalizedPath("/")} className="flex-shrink-0 group">
            <img 
              src={hausLogo} 
              alt="HAUS" 
              className="h-10 lg:h-12 w-auto transition-opacity duration-300 group-hover:opacity-80"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden min-[1700px]:flex items-center gap-0.5">
            {/* All Categories + Shop Categories with hover dropdowns */}
            <NavigationMenu>
              <NavigationMenuList className="gap-0">
                {/* Individual category dropdowns - Order: Bathroom, Kitchen, Lighting, Electricity, Hardware, Accessories */}
                {mergedCategories.map((cat) => (
                  <NavigationMenuItem key={cat.label}>
                    <NavigationMenuTrigger 
                      onClick={() => navigate(cat.href)}
                      className={cn(
                        "text-xs font-medium transition-colors duration-300 bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent cursor-pointer px-1.5",
                        "text-cream/70 hover:text-cream data-[state=open]:text-cream"
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {cat.icon && <cat.icon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.6} />}
                        {cat.label}
                      </span>
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className="z-[100]">
                      {cat.isLarge ? (
                        /* Large category dropdown - clean grid layout without image */
                        <div className="bg-charcoal border border-charcoal-light/20 rounded-lg shadow-elevated p-5 w-auto min-w-[540px] max-w-[680px] max-h-[calc(100vh-100px)] overflow-y-auto">
                          <NavigationMenuLink asChild>
                            <Link 
                              to={cat.href}
                              className="flex items-center gap-2 text-sm font-semibold text-cream hover:text-gold mb-4 transition-colors"
                            >
                              {cat.icon && <cat.icon className="h-4 w-4" strokeWidth={1.6} />}
                              {t.product.viewAll} {cat.label}
                            </Link>
                          </NavigationMenuLink>
                          <div className="grid grid-cols-3 gap-x-6 gap-y-3">
                            {cat.groups.map((group, groupIndex) => (
                              <div key={group.title || groupIndex} className="space-y-1">
                                {group.title && (
                                  <NavigationMenuLink asChild>
                                    <Link
                                      to={group.href}
                                      className="block text-[11px] font-bold text-gold hover:text-cream uppercase tracking-wider mb-2 transition-colors"
                                    >
                                      {group.title}
                                    </Link>
                                  </NavigationMenuLink>
                                )}
                                {group.items.map((sub) => (
                                  <NavigationMenuLink key={sub.label} asChild>
                                    <Link 
                                      to={sub.href}
                                      className="flex items-center gap-2 text-xs text-cream/60 hover:text-cream py-0.5 transition-all duration-200"
                                    >
                                      <span>{sub.label}</span>
                                    </Link>
                                  </NavigationMenuLink>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        /* Standard category dropdown - compact list without image */
                        <div className="bg-charcoal border border-charcoal-light/20 rounded-lg shadow-elevated p-3 min-w-[200px] max-h-[calc(100vh-100px)] overflow-y-auto">
                          <NavigationMenuLink asChild>
                            <Link 
                              to={cat.href}
                              className="flex items-center gap-2 px-2 py-2 text-sm font-semibold text-cream hover:text-gold transition-colors"
                            >
                              {cat.icon && <cat.icon className="h-4 w-4" strokeWidth={1.6} />}
                              {t.product.viewAll}
                            </Link>
                          </NavigationMenuLink>
                          <div className="my-2 border-t border-charcoal-light/30" />
                          <div className="space-y-0.5">
                            {cat.groups.flatMap(g => g.items).map((sub) => (
                              <NavigationMenuLink key={sub.label} asChild>
                                <Link 
                                  to={sub.href}
                                  className="flex items-center gap-2 px-2 py-1.5 text-xs text-cream/60 hover:text-cream transition-all duration-200"
                                >
                                  <span>{sub.label}</span>
                                </Link>
                              </NavigationMenuLink>
                            ))}
                          </div>
                        </div>
                      )}
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ))}

                {/* Brands: direct link to the brands page (no dropdown) */}
                {brands.length > 0 && (
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild>
                      <Link
                        to={getLocalizedPath('/brands')}
                        className={cn(
                          "inline-flex items-center text-xs font-medium transition-colors duration-300 px-2 py-2",
                          "text-cream/70 hover:text-cream"
                        )}
                      >
                        {language === 'lv' ? 'Zīmoli' : language === 'ru' ? 'Бренды' : 'Brands'}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                )}



              </NavigationMenuList>
            </NavigationMenu>

            <Link 
              to={getLocalizedPath("/shop?tag=sale")} 
              className="relative text-xs font-bold py-2 px-2 uppercase tracking-wider group transition-all duration-300 hover:scale-110"
            >
              <span className="text-red-500 drop-shadow-[0_2px_8px_rgba(239,68,68,0.6)] group-hover:drop-shadow-[0_4px_16px_rgba(239,68,68,0.8)] transition-all duration-300">
                {t.nav.sale}
              </span>
            </Link>
          </nav>

          {/* Search, Business, Cart, Language & Mobile Menu */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <div ref={searchRef} className="relative hidden sm:block">
              {searchOpen ? (
                <div className="relative">
                  <form onSubmit={handleSearchSubmit} className="relative">
                    <Input
                      type="text"
                      placeholder={t.shop?.searchPlaceholder || "Search products..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="w-64 bg-charcoal/80 border-cream/20 text-cream placeholder:text-cream/50 focus:border-gold pr-10"
                      autoFocus
                    />
                    <Button
                      type="submit"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full text-cream/50 hover:text-gold"
                      disabled={!searchQuery.trim()}
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  </form>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative group"
                  onClick={() => setSearchOpen(true)}
                  aria-label={t.shop?.searchPlaceholder || "Search products"}
                >
                  <Search className="h-5 w-5 transition-colors text-cream group-hover:text-gold" />
                </Button>
              )}
            </div>

            {/* Business link */}
            <Link 
              to={isBusinessCustomer ? `/${language}/business/dashboard` : `/${language}/business/auth`}
              className="hidden sm:flex"
            >
              <Button
                variant="ghost"
                className="relative group flex items-center gap-1.5 px-2"
                aria-label={isBusinessCustomer ? t.business.dashboard : t.business.login}
              >
                <Building2 className={cn(
                  "h-4 w-4 transition-colors",
                  isApprovedBusinessCustomer ? "text-copper" : "text-cream group-hover:text-gold"
                )} />
                <span className={cn(
                  "text-xs font-medium transition-colors",
                  isApprovedBusinessCustomer ? "text-copper" : "text-cream group-hover:text-gold"
                )}>B2B</span>
              </Button>
            </Link>

            <LanguageSwitcher />

            {/* Wishlist */}
            <Button
              variant="ghost"
              size="icon"
              className="relative group"
              onClick={() => setWishlistOpen(true)}
              aria-label="Wishlist"
            >
              <Heart className="h-5 w-5 transition-colors text-cream group-hover:text-red-500" />
              {wishlistItems > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-gold text-charcoal text-[10px] flex items-center justify-center font-semibold">
                  {wishlistItems}
                </span>
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              className="relative group"
              onClick={() => setCartOpen(true)}
              aria-label={t.cart?.title || "Shopping cart"}
            >
              <ShoppingCart className="h-5 w-5 transition-colors text-cream group-hover:text-gold" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-gold text-charcoal text-[10px] flex items-center justify-center font-semibold">
                  {totalItems}
                </span>
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="min-[1700px]:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5 text-cream" />
              ) : (
                <Menu className="h-5 w-5 text-cream" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="min-[1700px]:hidden py-4 border-t mt-4 animate-fade-in border-cream/10 bg-charcoal/95 backdrop-blur-sm -mx-4 px-4 max-h-[calc(100vh-120px)] overflow-y-auto">
            <div className="flex flex-col gap-2">
              {/* Mobile Search */}
              <div className="relative mb-4">
                <form onSubmit={handleSearchSubmit} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-cream/50" />
                    <Input
                      type="text"
                      placeholder={t.shop?.searchPlaceholder || "Search products..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-cream/5 border-cream/20 text-cream placeholder:text-cream/40"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="sm"
                    className="bg-gold text-charcoal hover:bg-gold/90"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </form>
              </div>

              <Link 
                to={getLocalizedPath("/")} 
                className="text-base font-medium py-3 px-4 rounded-xl transition-colors text-cream hover:bg-cream/5"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t.nav.home}
              </Link>
              
              
              {/* Collapsible Categories */}
              {mergedCategories.map((cat) => (
                <Collapsible key={cat.label} className="border-b border-cream/10 last:border-b-0">
                  <div className="flex items-center">
                    <Link 
                      to={cat.href} 
                      className="flex flex-1 items-center gap-2 text-base font-semibold py-3 px-4 transition-colors text-cream hover:text-gold"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {cat.icon && <cat.icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.6} />}
                      {cat.label}
                    </Link>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-10 w-10 p-0 text-cream/60 hover:text-gold hover:bg-cream/5">
                        <ChevronDown className="h-4 w-4 transition-transform duration-200 [&[data-state=open]>svg]:rotate-180" />
                        <span className="sr-only">Toggle</span>
                      </Button>
                    </CollapsibleTrigger>
                  </div>
                  <CollapsibleContent className="pl-4 pb-2">
                    <div className="flex flex-col gap-1">
                      {cat.groups.map((group, groupIndex) => (
                        <div key={group.title || groupIndex}>
                          {group.title && (
                            <Link
                              to={group.href}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block text-xs font-semibold text-gold/80 hover:text-gold mt-2 mb-1 px-4 uppercase tracking-wider"
                            >
                              {group.title}
                            </Link>
                          )}
                          {group.items.map((sub) => (
                            <Link 
                              key={sub.label}
                              to={sub.href} 
                              className="flex items-center gap-2 text-sm py-2 px-4 rounded-lg transition-colors text-cream/60 hover:text-cream hover:bg-cream/5"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              <span>{sub.label}</span>
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              ))}

              {/* Brands link in mobile */}
              {brands.length > 0 && (
                <Link
                  to={getLocalizedPath('/brands')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block border-b border-cream/10 text-base font-semibold py-3 px-4 text-cream hover:text-gold transition-colors"
                >
                  {language === 'lv' ? 'Zīmoli' : language === 'ru' ? 'Бренды' : 'Brands'}
                </Link>
              )}



              {/* Sale link in mobile */}
              <Link 
                to={getLocalizedPath("/shop?tag=sale")} 
                className="text-base font-bold py-3 px-4 rounded-xl transition-colors block hover:bg-red-500/10 uppercase tracking-wider"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="text-red-500 drop-shadow-[0_2px_8px_rgba(239,68,68,0.6)]">
                  🔥 {t.nav.sale}
                </span>
              </Link>
              
              {/* Business link in mobile menu */}
              <div className="border-t border-cream/10 pt-4 mt-2">
                <Link 
                  to={isBusinessCustomer ? `/${language}/business/dashboard` : `/${language}/business/auth`}
                  className={cn(
                    "flex items-center gap-3 text-base font-medium py-3 px-4 rounded-xl transition-colors",
                    isApprovedBusinessCustomer ? "text-copper" : "text-cream hover:bg-cream/5"
                  )}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Building2 className="h-5 w-5" />
                  {isBusinessCustomer ? t.business.dashboard : t.business.login}
                </Link>
              </div>
            </div>
          </nav>
        )}
      </div>

      <CartDrawer />
      <WishlistDrawer />
    </header>
  );
};

